import type { Question } from '../types';

// Part 1: Enhanced Giver Onboarding Questions (Evidence-Based)
export const giverQuestions: Question[] = [
  {
    id: 1,
    text: "Hi! I'm Grace, your personal gift curator. What should I call you?",
    type: "name"
  },
  {
    id: 2,
    text: "Any cultural gift-giving customs I should respect?",
    type: "name",
    hints: ["Christmas", "Diwali", "Eid", "Hanukkah", "Lunar New Year", "None", "Other traditions"]
  },
  {
    id: 3,
    text: "Think about the last gift you gave. How did that experience feel?",
    type: "confidence",
    hints: ["😰 Stressful process", "😐 Straightforward task", "😊 Enjoyable experience"]
  },
  {
    id: 4,
    text: "What drove you to choose that particular gift?",
    type: "values",
    multiple: true,
    maxSelections: 2,
    hints: [
      { text: "Show how much I care", icon: "❤️" },
      { text: "Make them genuinely happy", icon: "😊" },
      { text: "Find something perfect for them", icon: "🎯" },
      { text: "Give something impressive", icon: "⭐" },
      { text: "Solve a problem they had", icon: "🔧" }
    ]
  },
  {
    id: 5,
    text: "Your friend loves a very unusual item you've never seen before. In the store, you're drawn to:",
    type: "style",
    hints: [
      "The unusual item - if they love it, I trust them",
      "Something unique but more widely appealing", 
      "A balance - unique but with broader appeal",
      "Something I know they'll definitely like",
      "A classic choice I'm confident in"
    ]
  },
  {
    id: 6,
    text: "When you're stressed, what kind of support helps you most?",
    type: "attachment_cue",
    hints: [
      { text: "Quality time together", icon: "⏰" },
      { text: "Someone helping with tasks", icon: "🔧" },
      { text: "Thoughtful surprises", icon: "🎁" },
      { text: "Words of encouragement", icon: "💬" },
      { text: "Physical comfort", icon: "🤗" }
    ]
  },
  {
    id: 7,
    text: "Your friend says: 'I don't care how much it costs, I just want them to know I care.' Your reaction?",
    type: "values",
    multiple: true,
    maxSelections: 2,
    hints: [
      { text: "Totally agree - it's about the thought", icon: "💭" },
      { text: "I'd suggest looking for deals first", icon: "🎯" },
      { text: "Sometimes you do need to spend more", icon: "💸" },
      { text: "The right gift makes the right impression", icon: "⭐" },
      { text: "Better to get one quality thing", icon: "💎" }
    ]
  },
  {
    id: 8,
    text: "It's December 20th and you haven't bought gifts yet. What happens in your mind?",
    type: "personality",
    hints: [
      "Panic mode - this is stressful!",
      "Time to make a strategic plan",
      "No problem - I work well under pressure",
      "Online shopping to the rescue"
    ]
  },
  {
    id: 9,
    text: "When you gave a gift and weren't sure how it was received, what did you do?",
    type: "personality",
    hints: [
      "I checked in subtly to see if they liked it",
      "I asked directly if it was what they wanted", 
      "I trusted they'd tell me if there were issues",
      "I made a mental note for next time"
    ]
  }
];

// vNext Enhanced Giver Questions (7-question, 4-minute flow)
export const giverQuestionsVNext: Question[] = [
  {
    id: 1,
    text: "What should I call you, and are there any cultural customs I should respect when thinking about gifts?",
    type: "name_and_culture",
    hints: ["Name + any traditions", "Christmas", "Diwali", "Hanukkah", "No specific customs", "Other traditions"]
  },
  {
    id: 2, 
    text: "When it's gift-time, do you typically feel stressed, neutral, or excited about the process?",
    type: "giver_confidence",
    hints: ["😰 Stressed", "😐 Neutral", "😊 Excited"]
  },
  {
    id: 3,
    text: "Which option best matches why you're gifting this time?",
    type: "giver_motive",
    hints: [
      "① It's tradition - keeping customs and expectations",
      "② Show I care about them - focused on their wellbeing", 
      "③ Share a bit of me - expressing my personality or taste",
      "④ Tell a bigger story - marking something meaningful between us"
    ]
  },
  {
    id: 4,
    text: "Do you plan weeks ahead or look for inspiration closer to the date?", 
    type: "planning_style",
    hints: ["📅 Plan ahead", "⚡ Last-minute inspiration", "🔄 Mix of both"]
  },
  {
    id: 5,
    text: "What's a comfortable price range—and is that flexible if we find the perfect thing?",
    type: "budget_with_flex", 
    hints: ["$0-50 (firm)", "$50-200 (flexible)", "$200+ (very flexible)", "No set limit"]
  },
  {
    id: 6,
    text: "Pick one feeling you hope this gift sparks:",
    type: "desired_emotion",
    hints: ["Surprised", "Supported", "Cherished", "Delighted", "Appreciated", "Other (specify)"]
  },
  {
    id: 7,
    text: "Quick preference sliders:",
    type: "personality_sliders",
    sliderConfig: [
      { label: "Creative & Unique ⇄ Classic & Reliable", key: "openness_pos", min: 0, max: 100 },
      { label: "Shared Experiences ⇄ Private Moments", key: "extraversion_pos", min: 0, max: 100 }
    ]
  }
];

// Part 2: Recipient Profiling - Quick Add (4 questions)
export const recipientQuickQuestions: Question[] = [
  {
    id: 101,
    text: "Tell me about your relationship with this person:",
    type: "relationship",
    hints: ["💕 Romantic partner", "👪 Close family", "👯 Close friend", "💼 Work colleague", "🤝 Acquaintance", "📝 Other"]
  },
  {
    id: 102,
    text: "How long have you known each other?",
    type: "confidence",
    hints: ["<6 months", "6 months - 2 years", "2-5 years", "5+ years"]
  },
  {
    id: 104,
    text: "Choose 3 words that best describe them:",
    type: "values",
    multiple: true,
    maxSelections: 3,
    hints: [
      { text: "Creative soul", icon: "🎨" },
      { text: "Quality perfectionist", icon: "💎" },
      { text: "Adventure seeker", icon: "🏔️" },
      { text: "Practical helper", icon: "🔧" },
      { text: "Memory keeper", icon: "📸" },
      { text: "Social connector", icon: "🎉" },
      { text: "Wellness focused", icon: "🧘" },
      { text: "Tech enthusiast", icon: "💻" },
      { text: "Minimalist thinker", icon: "⚪" },
      { text: "Family centered", icon: "👨‍👩‍👧‍👦" },
      { text: "Culture explorer", icon: "🌍" },
      { text: "Collection builder", icon: "🎯" },
      { text: "Eco conscious", icon: "🌱" },
      { text: "Status aware", icon: "⭐" }
    ]
  },
  {
    id: 105,
    text: "What's your typical budget for gifts to them?",
    type: "confidence",
    hints: ["💰 $ ($0-50)", "💰💰 $$ ($50-200)", "💰💰💰 $$$ ($200+)", "🎁 No set limit"]
  },
  {
    id: 106,
    text: "Do they prefer experiences or physical gifts?",
    type: "confidence",
    hints: ["🎪 Experiences", "📦 Physical items", "🎭 Both equally", "🤷 Not sure"]
  }
];

// vNext Enhanced Recipient Questions (discovery-focused)
export const recipientQuestionsVNext: Question[] = [
  {
    id: 11,
    text: "What's been bringing them joy recently—new hobby, show, achievement, anything?",
    type: "current_joy",
    hints: ["New interest", "Recent achievement", "Show/book they love", "Work success", "Personal milestone", "Nothing specific"]
  },
  {
    id: 12,
    text: "Any struggle or problem they've mentioned lately? Great gifts solve real pains.",
    type: "problem_solve", 
    hints: ["Work stress", "Life transition", "Time management", "Health challenge", "Creative block", "None that I know of"]
  },
  {
    id: 13,
    text: "If describing them to a stranger, what's the first thing you'd say?",
    type: "free_text_persona",
    hints: ["Free text description of their essence"]
  },
  {
    id: 14,
    text: "What would they love but never buy for themselves?",
    type: "treat_for_self",
    hints: ["Luxury version of everyday item", "Self-care indulgence", "Aspirational purchase", "Professional upgrade", "Hobby enhancement", "Not sure"]
  },
  {
    id: 15,
    text: "Tell me about your relationship with them:",
    type: "relationship",
    hints: ["💕 Romantic partner", "👪 Close family", "👯 Close friend", "💼 Work colleague", "🤝 Acquaintance", "📝 Other"]
  },
  {
    id: 16,
    text: "How long have you known each other?",
    type: "relationship_duration",
    hints: ["<6 months", "6 months - 2 years", "2-5 years", "5+ years"]
  },
  {
    id: 17,
    text: "Quick personality check - pick their preference:",
    type: "personality_quick",
    hints: [
      "Free Saturday: favorite coffee shop OR exploring new neighborhood",
      "Perfect evening: hosting friends over OR quiet night in",
      "Big purchase: research extensively OR trust gut feeling"
    ]
  }
];

// Part 2: Recipient Profiling - Full Profile (11 questions)
export const recipientFullQuestions: Question[] = [
  {
    id: 201,
    text: "Tell me about your relationship with this person:",
    type: "relationship",
    hints: ["💕 Romantic partner", "👪 Close family", "👯 Close friend", "💼 Work colleague", "🤝 Acquaintance", "📝 Other"]
  },
  {
    id: 202,
    text: "How long have you known each other?",
    type: "confidence",
    hints: ["<6 months", "6 months - 2 years", "2-5 years", "5+ years"]
  },
  {
    id: 204,
    text: "Pick the weekend that sounds most like them:",
    type: "personality",
    hints: [
      "🏔️ Adventure sports or camping",
      "🎨 Art gallery or cooking class", 
      "🏠 Cozy game night at home",
      "🎉 Big group party or event"
    ]
  },
  {
    id: 205,
    text: "When they're stressed, what helps them most?",
    type: "attachment_cue",
    hints: [
      { text: "Quality time together", icon: "⏰" },
      { text: "Helpful acts of service", icon: "🔧" },
      { text: "Thoughtful surprises", icon: "🎁" },
      { text: "Words of encouragement", icon: "💬" },
      { text: "Physical comfort", icon: "🤗" }
    ]
  },
  {
    id: 206,
    text: "How does this person typically react to gifts?",
    type: "personality",
    hints: [
      "😍 Loves surprises and cherishes everything",
      "😊 Appreciative but has specific tastes",
      "😐 Polite but you can tell if they don't use it",
      "🔧 Practical - prefers useful over sentimental"
    ]
  },
  {
    id: 207,
    text: "If you gave them a cherished photo in a custom frame, they would...",
    type: "sentimentality",
    hints: [
      "💖 Treasure it forever",
      "😊 Display it for a while", 
      "🤔 Appreciate the thought but prefer practical gifts"
    ]
  },
  {
    id: 208,
    text: "Their attitude toward surprises?",
    type: "surprise",
    hints: [
      "🎉 Bring it on!",
      "🤔 Depends on the occasion",
      "😌 Small surprises only",
      "📝 Please ask first"
    ]
  },
  {
    id: 209,
    text: "Their style leans toward...",
    type: "aesthetic",
    hints: [
      "📚 Classic & timeless",
      "⚪ Modern minimalist",
      "🌈 Colorful & eclectic",
      "🔧 Practical function-first",
      "🤷 No strong preference"
    ]
  },
  {
    id: 210,
    text: "Would they pay extra for eco-friendly/ethical options?",
    type: "ethics",
    hints: ["🌱 Always", "✋ Sometimes", "💰 Rarely", "🎯 Price is the priority"]
  },
  {
    id: 211,
    text: "Converting digital memories to physical keepsakes?",
    type: "digital",
    hints: ["💝 Love the idea", "😊 It's nice sometimes", "📱 Prefer keeping things digital"]
  },
  {
    id: 212,
    text: "Select up to 3 areas they're focused on in life:",
    type: "focus",
    multiple: true,
    maxSelections: 3,
    hints: [
      { text: "Career & Work", icon: "💼" },
      { text: "Health & Fitness", icon: "🏃‍♀️" },
      { text: "Creative Expression", icon: "🎨" },
      { text: "Family & Relationships", icon: "👪" },
      { text: "Travel & Adventure", icon: "✈️" },
      { text: "Learning & Growth", icon: "📚" },
      { text: "Home & Living Space", icon: "🏡" },
      { text: "Social Impact", icon: "🌍" },
      { text: "Technology & Innovation", icon: "💻" },
      { text: "Hobbies & Collections", icon: "🎯" }
    ]
  },
  {
    id: 213,
    text: "Select up to 3 core values that resonate with them:",
    type: "values",
    multiple: true,
    maxSelections: 3,
    hints: [
      { text: "Quality over quantity", icon: "💎" },
      { text: "Latest trends", icon: "📈" },
      { text: "Unique/one-of-a-kind", icon: "✨" },
      { text: "Practical utility", icon: "🔧" },
      { text: "Prestige brands", icon: "👑" },
      { text: "Ethical sourcing", icon: "🌱" },
      { text: "Great value", icon: "💰" },
      { text: "Support small business", icon: "🤝" },
      { text: "Innovation", icon: "🚀" }
    ]
  },
  {
    id: 214,
    text: "Select up to 3 personality signals that best describe them:",
    type: "values",
    multiple: true,
    maxSelections: 3,
    hints: [
      { text: "Creative soul", icon: "🎨" },
      { text: "Tech enthusiast", icon: "💻" },
      { text: "Adventure seeker", icon: "🏔️" },
      { text: "Quality perfectionist", icon: "💎" },
      { text: "Memory keeper", icon: "📸" },
      { text: "Social connector", icon: "🎉" },
      { text: "Practical helper", icon: "🔧" },
      { text: "Culture explorer", icon: "🌍" },
      { text: "Wellness focused", icon: "🧘" },
      { text: "Minimalist thinker", icon: "⚪" },
      { text: "Status aware", icon: "⭐" },
      { text: "Collection builder", icon: "🎯" }
    ]
  },
  {
    id: 215,
    text: "How deep is their engagement with their main interests?",
    type: "confidence",
    hints: ["🌱 Casual explorer", "🎯 Focused hobbyist", "🔥 Serious enthusiast", "🏆 Expert level"]
  },
  {
    id: 216,
    text: "Select any restrictions or strong preferences they have:",
    type: "structured_interests",
    multiple: true,
    hints: [
      { text: "No dietary restrictions", icon: "🍽️" },
      { text: "Vegetarian/Vegan", icon: "🌱" },
      { text: "Food allergies", icon: "⚠️" },
      { text: "Eco-conscious only", icon: "🌍" },
      { text: "No clutter/minimalist", icon: "⚪" },
      { text: "Tech-friendly", icon: "💻" },
      { text: "Experiences over objects", icon: "🎪" },
      { text: "Local/small business only", icon: "🏪" },
      { text: "Luxury preferences", icon: "💎" },
      { text: "Budget-sensitive", icon: "💰" },
      { text: "Other restrictions", icon: "📝" }
    ]
  },
  {
    id: 217,
    text: "Any gift stories or specific interests to share? (Optional)",
    type: "interests",
    hints: ["Skip this question", "They loved when I gave them...", "They've mentioned wanting...", "They collect or are passionate about..."]
  }
];

// Part 3: Gift Occasion - Quick Flow (3 questions)
export const occasionQuickQuestions: Question[] = [
  {
    id: 301,
    text: "What's the occasion for this gift?",
    type: "occasion",
    hints: ["🎂 Birthday", "💕 Anniversary", "🎁 Holiday", "🎉 Celebration", "😅 Apology", "💝 Just because", "📝 Other"]
  },
  {
    id: 302,
    text: "What's your budget for this gift?",
    type: "confidence",
    hints: ["$0-25", "$25-50", "$50-100", "$100-200", "$200-500", "$500+", "No limit"]
  },
  {
    id: 303,
    text: "What feeling are you hoping to create?",
    type: "emotion",
    hints: [
      { text: "Surprise & delight", icon: "✨" },
      { text: "Show you care", icon: "❤️" },
      { text: "Make them laugh", icon: "😄" },
      { text: "Help them feel appreciated", icon: "🙏" },
      { text: "Solve a practical need", icon: "🔧" }
    ]
  }
];

// Part 3: Gift Occasion - Standard Flow (5 questions)
export const occasionStandardQuestions: Question[] = [
  {
    id: 401,
    text: "What's the occasion for this gift?",
    type: "occasion",
    hints: ["🎂 Birthday", "💕 Anniversary", "🎁 Holiday", "🎉 Celebration", "😅 Apology", "💝 Just because", "📝 Other"]
  },
  {
    id: 402,
    text: "What's your budget for this gift?",
    type: "confidence",
    hints: ["$0-25", "$25-50", "$50-100", "$100-200", "$200-500", "$500+", "No limit"]
  },
  {
    id: 403,
    text: "What do you want them to feel? (select up to 2)",
    type: "emotion",
    multiple: true,
    maxSelections: 2,
    hints: [
      { text: "Surprised", icon: "✨" },
      { text: "Understood", icon: "❤️" },
      { text: "Excited", icon: "🚀" },
      { text: "Supported", icon: "💪" },
      { text: "Happy", icon: "😊" },
      { text: "Relaxed", icon: "🧘" },
      { text: "Connected", icon: "🤝" },
      { text: "Inspired", icon: "💡" }
    ]
  },
  {
    id: 404,
    text: "Anything special about this occasion I should know?",
    type: "context",
    hints: ["First anniversary", "Big promotion", "Rough week", "New home", "Nothing special"]
  },
  {
    id: 405,
    text: "For this occasion, do you prefer a safe choice or something surprising?",
    type: "slider",
    sliderConfig: {
      min: 0,
      max: 100,
      step: 10,
      labels: ["Safe & Reliable", "Bold & Surprising"]
    },
    hints: ["Something they'll definitely love", "Something that might be amazing but riskier"]
  }
];

// vNext Enhanced Context Questions (relationship dynamics focus)
export const contextQuestionsVNext: Question[] = [
  {
    id: 21,
    text: "What are you celebrating AND how do you two relate?",
    type: "occasion_relationship_merge",
    hints: ["Birthday + close friends", "Holiday + family", "Anniversary + romantic", "Just because + colleagues", "Promotion + work friends"]
  },
  {
    id: 22,
    text: "Is either of you in a mentor or boss role?", 
    type: "power_dynamic",
    hints: ["Equal relationship", "I'm senior/mentor", "They're senior/mentor", "Complex dynamic"]
  },
  {
    id: 23,
    text: "Do gifts typically bounce back and forth between you?",
    type: "reciprocity",
    hints: ["Yes, we exchange gifts", "This is one-way", "Complex pattern", "Not sure"]
  },
  {
    id: 24,
    text: "When you joke around, your style is more:",
    type: "communication",
    hints: ["Playful teasing", "Heartfelt sharing", "Direct/straightforward", "Formal/polite"]
  },
  {
    id: 25,
    text: "Any inside joke or ritual I should reference?",
    type: "inside_joke", 
    hints: ["Yes (describe)", "No shared references"]
  },
  {
    id: 26,
    text: "If this gift could say one thing, which feels closest?",
    type: "symbol_intent",
    hints: ["'This is so you'", "'This is so us'", "'Keeping tradition'", "'Marks a turning point'"]
  },
  {
    id: 27,
    text: "Final budget range and timing:",
    type: "budget_timing_final",
    hints: ["Budget slider + date picker"]
  },
  {
    id: 28,
    text: "One word for how you want them to feel:",
    type: "tone_keyword",
    hints: ["Surprised", "Loved", "Appreciated", "Delighted", "Understood", "Celebrated", "Other"]
  }
];

// Legacy export for backward compatibility
export const questions = giverQuestions; 